/**
 * QualifierToggle — atom for the shot qualifier flags
 * (PAINT / FASTBREAK / SECOND_CHANCE).
 *
 * Renders three pill toggles. Active qualifiers are passed in via props.
 * Toggling is dispatched via onToggle(qualifier).
 *
 * The qualifier list is mirrored from useStatsSession.activeQualifiers.
 * The default labels are i18n keys under estadisticasNew.* — pass `t` to
 * translate, otherwise English fallbacks are used.
 */

import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import { QUALIFIERS } from '../constants';

const FALLBACK_LABELS = {
  PAINT: 'Paint',
  FASTBREAK: 'Fast break',
  SECOND_CHANCE: '2nd chance',
};

const QUALIFIER_ORDER = [QUALIFIERS.PAINT, QUALIFIERS.FASTBREAK, QUALIFIERS.SECOND_CHANCE];

function getLabel(qualifier, t) {
  if (typeof t === 'function') {
    const key = `estadisticasNew.${qualifier === 'PAINT' ? 'paint' : qualifier === 'FASTBREAK' ? 'fastbreak' : 'secondChance'}`;
    return t(key, { defaultValue: FALLBACK_LABELS[qualifier] });
  }
  return FALLBACK_LABELS[qualifier];
}

export default function QualifierToggle({ active = [], onToggle, t, size = 'sm' }) {
  const theme = useStatsTheme();
  const color = theme.actionColors.warning;

  const heights = { sm: 28, md: 34 };
  const fontSizes = { sm: 10, md: 12 };

  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {QUALIFIER_ORDER.map((q) => {
        const isActive = active.includes(q);
        return (
          <ButtonBase
            key={q}
            onClick={() => typeof onToggle === 'function' && onToggle(q)}
            aria-pressed={isActive}
            sx={{
              height: heights[size],
              px: 1.2,
              borderRadius: theme.radius.pill,
              backgroundColor: isActive ? withAlpha(color, 0.36) : withAlpha(color, 0.08),
              border: `1px solid ${withAlpha(color, isActive ? 0.65 : 0.22)}`,
              color: theme.text.primary,
              fontFamily: theme.typography.fontFamily,
              fontSize: fontSizes[size],
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              transition: `background-color ${theme.motion.fast}`,
              '&:hover': { backgroundColor: withAlpha(color, isActive ? 0.45 : 0.18) },
            }}
          >
            <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
              {getLabel(q, t)}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
